import React, { useEffect, useRef, useState } from 'react'
import './OrganizaceVyuky.scss'

const OrganizaceVyuky = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [activeTab, setActiveTab] = useState('organizace')
  const sectionRef = useRef(null)

   useEffect(() => {
    // Okamžitě zobraz na mobilech (< 768px)
    if (window.innerWidth < 992) {
      setIsVisible(true)
      return
    }

    // Pro desktop použij Intersection Observer
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '50px' // Trigger trochu dříve
      }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    // Fallback - zobraz po 2 sekundách pokud observer nefunguje
    const fallbackTimer = setTimeout(() => {
      setIsVisible(true)
    }, 2000)

    return () => {
      observer.disconnect()
      clearTimeout(fallbackTimer)
    }
  }, [])

  // Také reaguj na resize - pokud se změní na mobil, zobraz okamžitě
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 992 && !isVisible) {
        setIsVisible(true)
      }
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isVisible])

  const courseOrganization = [
    {
      number: '01',
      title: 'Registrace',
      description: 'Přihlášení do kurzu a zařazení do pořadníku uchazečů.',
      icon: '📝',
      details: [
        'Registrace vytváří místo v pořadí přihlášení',
        'Garance účasti na praktických jízdách',
      ]
    },
    {
      number: '02',
      title: 'Zahájení teoretické výuky',
      description: 'Teoretická výuka nového kurzu začíná ke konci praktických jízd probíhajícího kurzu.',
      icon: '📚',
      details: [
        'Při plné obsazenosti kurz začíná podle pořadí',
        'Individuální studijní plán'
      ]
    },
    {
      number: '03',
      title: 'Start praktických jízd',
      description: 'Praktické jízdy začínají, když se předchozí kurz chystá k závěrečným zkouškám.',
      icon: '🚗',
      details: [
        'Celkem 14 jízd - každá trvá 2x45 minut',
        'První 2 hodiny na autocvičišti Pánov',
        'Doprava na autocvičiště zdarma',
        'Obvyklá doba trvání: 2 měsíce'
      ]
    },
  ]

  const practicalDrivingInfo = [
    {
      title: 'První polovina jízd',
      timeframe: '7 jízd po 2×45 minutách',
      timing: 'Odpoledne po práci nebo škole',
      description: 'Autoškola vám maximálně vyjde vstříc a přizpůsobí se vašim časovým možnostem.',
      icon: '🌅',
      flexibility: 'Vysoká flexibilita'
    },
    {
      title: 'Druhá polovina jízd',
      timeframe: '7 jízd po 2×45 minutách',
      timing: 'Dopolední hodiny',
      description: 'Jízdy probíhají přednostně dopoledne, abyste si zvykli na provoz v čase, kdy pojedete zkoušku.',
      icon: '🌄',
      flexibility: 'Pevný čas'
    }
  ]

  const requiredDocuments = [
    {
      name: 'Posudek o zdravotní způsobilosti',
      fullName: 'Posudek o zdravotní způsobilosti k řízení motorových vozidel',
      description: 'Vyplní váš ošetřující lékař podle přílohy č. 2 vyhlášky č. 277/2004 Sb.',
      downloadUrl: '/documents/lekar_posudek.pdf',
      icon: '🏥',
      tip: 'Praktičtí lékaři nemusí mít aktuální formulář platný od roku 2024'
    },
    {
      name: 'Žádost o přijetí k výuce a výcviku',
      fullName: 'Žádost o přijetí k výuce a výcviku',
      description: 'Po vyplnění formuláře neprodleně kontaktuje Autoškolu Příkaský.',
      downloadUrl: '/documents/zadost_o_prijeti.pdf',
      sampleUrl: '/documents/zadost_vzor.pdf',
      icon: '📋',
      tip: 'Vytiskněte oboustranně na jeden list papíru a vyplňte podle VZORU'
    }
  ]

  const paymentInfo = {
    methods: ['Hotovost při první hodině', 'Bankovní převod'],
    account: 'Fio banka: 2500 758 239/2010',
    timing: 'Splatné při první zahajovací hodině'
  }

  const handleContactClick = () => {
    const element = document.getElementById('kontakt')
    if (element) {
      const headerHeight = window.innerWidth < 768 ? 80 : 100
      const elementPosition = element.offsetTop
      const offsetPosition = elementPosition - headerHeight

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className={`organization-section ${isVisible ? 'visible' : ''}`} ref={sectionRef}>
      <div className="section-content">
        {/* Header */}
        <div className="organization-header">
          <h2>Organizace výuky</h2>
          <p>Jak probíhá kurz v Autoškole Příkaský - od registrace až po závěrečné zkoušky</p>
        </div>

        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button 
            className={`tab-btn ${activeTab === 'organizace' ? 'active' : ''}`}
            onClick={() => setActiveTab('organizace')}
          >
            <span className="tab-icon">🎯</span>
            Průběh kurzu
          </button>
          <button 
            className={`tab-btn ${activeTab === 'praktika' ? 'active' : ''}`}
            onClick={() => setActiveTab('praktika')}
          >
            <span className="tab-icon">🚗</span>
            Praktické jízdy
          </button>
          <button 
            className={`tab-btn ${activeTab === 'dokumenty' ? 'active' : ''}`}
            onClick={() => setActiveTab('dokumenty')}
          >
            <span className="tab-icon">📄</span>
            Dokumenty
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {/* Organizace kurzu */}
          {activeTab === 'organizace' && (
            <div className="tab-panel">
              <div className="organization-steps">
                {courseOrganization.map((step, index) => (
                  <div key={index} className="step-card">
                    <div className="step-number">{step.number}</div>
                    <div className="step-content">
                      <div className="step-header">
                        <div className="step-icon">{step.icon}</div>
                        <h3>{step.title}</h3>
                      </div>
                      <p className="step-description">{step.description}</p>
                      <ul className="step-details">
                        {step.details.map((detail, idx) => (
                          <li key={idx}>{detail}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>

              <div className="guarantee-box">
                <div className="guarantee-icon">✅</div>
                <div className="guarantee-content">
                  <h4>Naše garance</h4>
                  <p>
                    Organizace Autoškoly Příkaský je garantem účasti uchazeče na praktických jízdách kurzu. 
                    <strong> Nedochází k situaci, kdy se přihlásíte do kurzu a na praktickou jízdu se nemůžete dostat.</strong>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Praktické jízdy */}
          {activeTab === 'praktika' && (
            <div className="tab-panel">
              <div className="driving-phases">
                {practicalDrivingInfo.map((phase, index) => (
                  <div key={index} className="phase-card">
                    <div className="phase-header">
                      <div className="phase-icon">{phase.icon}</div>
                      <div className="phase-info">
                        <h3>{phase.title}</h3>
                        <div className="phase-meta">
                          <span className="timeframe">{phase.timeframe}</span>
                          <span className="timing">{phase.timing}</span>
                        </div>
                      </div>
                      <div className="flexibility-badge">
                        {phase.flexibility}
                      </div>
                    </div>
                    <p className="phase-description">{phase.description}</p>
                  </div>
                ))}
              </div>

              <div className="alternative-option">
                <div className="option-header">
                  <h3>Nevyhovuje vám tato organizace?</h3>
                  <p>Máme pro vás řešení</p>
                </div>
                <div className="solitaire-offer">
                  <div className="offer-icon">⭐</div>
                  <div className="offer-content">
                    <h4>Kurz SOLITÉR</h4>
                    <p>
                      Všechny jízdy odjezdíte v časech, které vám maximálně vyhovují. 
                      Absolutní přednost v plánování i u zkoušek.
                    </p>
                    <button 
                      className="btn btn-primary"
                      onClick={() => {
                        const element = document.getElementById('cenik')
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' })
                        }
                      }}
                    >
                      Více o kurzu SOLITÉR
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Dokumenty a přihláška */}
          {activeTab === 'dokumenty' && (
            <div className="tab-panel">
              <div className="documents-intro">
                <h3>K přihlášení do kurzu potřebujete</h3>
                <p>Připravte si tyto dva formuláře pro úspěšné zahájení kurzu</p>
              </div>

              <div className="documents-grid">
                {requiredDocuments.map((doc, index) => (
                  <div key={index} className="document-card">
                    <div className="document-header">
                      <div className="document-icon">{doc.icon}</div>
                      <h4>{doc.name}</h4>
                    </div>
                    
                    <div className="document-body">
                      <h5>{doc.fullName}</h5>
                      <p className="document-description">{doc.description}</p>
                      
                      <div className="document-tip">
                        <span className="tip-icon">💡</span>
                        <span>
                          {doc.name === 'Žádost o přijetí k výuce a výcviku' ? (
                            <>
                              Vytiskněte oboustranně na jeden list papíru a vyplňte podle{' '}
                              <a 
                                href={doc.sampleUrl} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="sample-link"
                              >
                                VZORU
                              </a>
                            </>
                          ) : (
                            doc.tip
                          )}
                        </span>
                      </div>
                    </div>
                    
                    <div className="document-footer">
                      <a href={doc.downloadUrl} target="_blank" className="btn btn-outline download-btn">
                        <span className="download-icon">⬇️</span>
                        Stáhnout formulář
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              <div className="payment-info">
                <h4>Platba kurzovného</h4>
                <div className="payment-methods">
                  <div className="payment-method">
                    <span className="method-icon">💰</span>
                    <span>Hotovost při první zahajovací hodině</span>
                  </div>
                  <div className="payment-method">
                    <span className="method-icon">🏦</span>
                    <span>Bankovní převod na účet: {paymentInfo.account}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Aktuální informace */}
        <div className="current-info">
          <div className="info-header">
            <h3>Aktuální informace o kurzech</h3>
            <p>Pro nejnovější informace o volných místech a termínech</p>
          </div>
          
          <div className="info-channels">
            <a href="https://www.facebook.com/p/Auto%C5%A1kola-P%C5%99%C3%ADkask%C3%BD-Rat%C3%AD%C5%A1kovice-Kyjov-a-Hodon%C3%ADn-100051923246952/?locale=cs_CZ" target='_blank'>
              <div className="info-channel socials">
                <div className="channel-icon">📱</div>
                <h4>Sociální sítě</h4>
                <p>Sledujte naše <a href="https://www.facebook.com/p/Auto%C5%A1kola-P%C5%99%C3%ADkask%C3%BD-Rat%C3%AD%C5%A1kovice-Kyjov-a-Hodon%C3%ADn-100051923246952/?locale=cs_CZ" target='_blank'>Facebook stránky</a> pro aktuální informace o kurzech</p>
              </div>
            </a>
            
            <a href="tel:728108824">
              <div className="info-channel">
                  <div className="channel-icon">📞</div>
                  <h4>Telefonicky</h4>
                  <p>Zavolejte nám na</p>
                  <a href="tel:728108824" className="contact-link">728 108 824</a>
              </div>
             </a>
            
            <div className="info-channel">
              <div className="channel-icon">🏢</div>
              <h4>Osobně</h4>
              <p>Navštivte nás na adrese</p>
              <span className="address">Školní 277, Ratíškovice</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="organization-cta">
          <div className="cta-content">
            <h3>Připraveni začít?</h3>
            <p>Kontaktujte nás pro registraci do kurzu a vytvoření místa v pořadníku</p>
            <div className="cta-buttons">
              <a href="tel:728108824" className="btn btn-primary">
                <span className="phone-icon">📞</span>
                Zavolat: 728 108 824
              </a>
              <button className="btn btn-secondary" onClick={handleContactClick}>
                <span className="email-icon">✉️</span>
                Napsat zprávu
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Background decorations */}
      <div className="organization-background">
        <div className="bg-shape shape-1"></div>
        <div className="bg-shape shape-2"></div>
        <div className="bg-shape shape-3"></div>
      </div>
    </div>
  )
}

export default OrganizaceVyuky